import { useState, useEffect } from 'react';
import { Loader2, Users } from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import { jobService } from '@core/supabase/services/jobService';
import { candidateService } from '@core/supabase/services/candidateService';
import { applicationService } from '@core/supabase/services/applicationService';
import type { Job } from '@core/types';

/**
 * Bulk deterministic skill fitment for selected candidates vs one job (§9.5 / MAT-020).
 * Creates an application when missing, then writes fitment_score / fitment_json.
 */
export function CandidateBulkFitmentModal({
  open,
  onClose,
  orgId,
  userId,
  candidateIds,
  onComplete,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  userId: string;
  candidateIds: string[];
  onComplete: () => void;
}) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobId, setJobId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [log, setLog] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !orgId) return;
    void jobService.list(orgId).then((r) => {
      const list = Array.isArray(r) ? r : (r as { data: Job[] }).data;
      setJobs(list);
    });
  }, [open, orgId]);

  useEffect(() => {
    if (open) {
      setErr('');
      setLog([]);
      setJobId('');
    }
  }, [open]);

  const run = async () => {
    const job = jobs.find((j) => j.id === jobId);
    if (!job) {
      setErr('Select a job.');
      return;
    }
    setBusy(true);
    setErr('');
    const lines: string[] = [];
    try {
      for (const cid of candidateIds) {
        const cand = await candidateService.getById(cid);
        if (!cand) {
          lines.push(`${cid}: not found`);
          continue;
        }
        const skills = cand.parsed_skills ?? [];
        try {
          await applicationService.upsertApplicationWithSkillFitment({
            orgId,
            userId,
            candidateId: cid,
            job,
            candidateSkillList: skills,
          });
          lines.push(`${cand.full_name}: fitment updated`);
        } catch (e: unknown) {
          lines.push(`${cand.email}: ${e instanceof Error ? e.message : 'failed'}`);
        }
      }
      setLog(lines);
      onComplete();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="[Bulk Skill Fitment]"
      subtitle="Skill overlap analysis for selected candidates vs one job"
      icon={<Users className="w-4 h-4" />}
      size="md"
    >
      <p className="text-xs text-gray-500 mb-3">
        Uses <strong className="text-gray-400">skill overlap</strong> vs the job’s required skills (same engine as intake
        wizard). Candidates without <strong className="text-gray-400">parsed_skills</strong> get low coverage — enrich
        profiles or import skills first.
      </p>
      <p className="text-xs text-gray-500 mb-2">
        Selected: <strong className="text-gray-300">{candidateIds.length}</strong> candidate(s)
      </p>
      <select className="input w-full text-sm mb-4" value={jobId} onChange={(e) => setJobId(e.target.value)}>
        <option value="">Select job…</option>
        {jobs.map((j) => (
          <option key={j.id} value={j.id}>
            {j.title}
          </option>
        ))}
      </select>
      {err ? <p className="text-xs text-red-400 mb-2">{err}</p> : null}
      {log.length > 0 && (
        <ul className="text-xs text-gray-400 max-h-36 overflow-y-auto border border-gray-800 rounded-lg p-2 mb-3 space-y-0.5">
          {log.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary text-sm" onClick={onClose} disabled={busy}>
          Close
        </button>
        <button type="button" className="btn-primary text-sm inline-flex items-center gap-1" disabled={busy} onClick={() => void run()}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Run fitment
        </button>
      </div>
    </Modal>
  );
}
